/**
 * Custom service icons for the dashboard Services grid.
 *
 * Each icon depicts its actual practice (kundli chart, yin-yang, zodiac
 * wheel, fanned tarot cards…) instead of a generic glyph. Drawn on the same
 * grid as lucide-react (24x24 viewBox, stroke 1.8, round caps) so they drop
 * into the existing gradient circles without any style changes.
 */
import { SVGProps } from 'react';

const base = {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
} as const;

/** North Indian kundli chart — square, diagonals, inscribed diamond */
export const VedicIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <rect x="3.5" y="3.5" width="17" height="17" rx="1" />
        <path d="M3.5 3.5 20.5 20.5M20.5 3.5 3.5 20.5" />
        <path d="M12 3.5 20.5 12 12 20.5 3.5 12Z" />
    </svg>
);

/** Two overlapping birth-chart rings with a spark where they meet */
export const CompatibilityIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <circle cx="8.5" cy="12" r="5.4" />
        <circle cx="15.5" cy="12" r="5.4" />
        <path d="M12 9.6l.62 1.56 1.56.62-1.56.62L12 13.96l-.62-1.56-1.56-.62 1.56-.62Z" fill="currentColor" stroke="none" />
    </svg>
);

/** Prashna — a question held inside the kundli diamond */
export const PrashanIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M12 2.75 21.25 12 12 21.25 2.75 12Z" />
        <path d="M9.6 9.4a2.5 2.5 0 0 1 4.85.8c0 1.65-2.45 2.4-2.45 2.4" />
        <path d="M12 15.9h.01" />
    </svg>
);

/** Soul spiral unwinding outward toward a spark of a former life */
export const PastLifeIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M13.25 12.5a1.25 1.25 0 0 1-2.5 0 2.5 2.5 0 0 1 5 0 3.75 3.75 0 0 1-7.5 0 5 5 0 0 1 10 0 6.25 6.25 0 0 1-6.25 6.25" />
        <path d="M5.5 4.9l.62 1.48 1.48.62-1.48.62-.62 1.48-.62-1.48-1.48-.62 1.48-.62Z" fill="currentColor" stroke="none" />
    </svg>
);

/** Almanac — a calendar holding the moon and a tithi star */
export const PanchangIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
        <path d="M8 3v3.5M16 3v3.5M3.5 9.75h17" />
        <path d="M10.6 12.4a3.5 3.5 0 1 0 4.35 4.35A4.4 4.4 0 0 1 10.6 12.4Z" />
        <path d="M16.9 12.9h.01" />
    </svg>
);

/** Zodiac wheel — twelve houses around the ecliptic */
export const WesternIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <circle cx="12" cy="12" r="8.75" />
        <circle cx="12" cy="12" r="3.4" />
        <path d="M15.4 12h5.35M12 15.4v5.35M8.6 12H3.25M12 8.6V3.25" />
        <path d="M14.4 14.4l3.79 3.79M9.6 14.4l-3.79 3.79M9.6 9.6 5.81 5.81M14.4 9.6l3.79-3.79" />
    </svg>
);

/** Yin and yang — the taijitu */
export const ChineseIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <circle cx="12" cy="12" r="8.75" />
        <path d="M12 3.25a4.375 4.375 0 0 1 0 8.75 4.375 4.375 0 0 0 0 8.75" />
        <circle cx="12" cy="7.6" r="1.05" fill="currentColor" stroke="none" />
        <circle cx="12" cy="16.4" r="1.05" />
    </svg>
);

/** Pinpoint target — KP's sub-lord precision timing */
export const KPIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <circle cx="12" cy="12" r="7.5" />
        <circle cx="12" cy="12" r="3.25" />
        <path d="M12 2.75v3M12 18.25v3M2.75 12h3M18.25 12h3" />
        <circle cx="12" cy="12" r="0.9" fill="currentColor" stroke="none" />
    </svg>
);

/** The Red Book — with a kundli diamond on its cover */
export const LalKitabIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
        <path d="M12.25 7.5 14.75 10l-2.5 2.5L9.75 10Z" />
    </svg>
);

/** Three fanned cards, the querent's card starred */
export const TarotIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M10.6 6.2H5.8A1.2 1.2 0 0 0 4.6 7.4v7.8a1.2 1.2 0 0 0 1.2 1.2h4.8" transform="rotate(-18 7.6 11.3)" />
        <path d="M13.4 6.2h4.8a1.2 1.2 0 0 1 1.2 1.2v7.8a1.2 1.2 0 0 1-1.2 1.2h-4.8" transform="rotate(18 16.4 11.3)" />
        <rect x="8.8" y="4.9" width="6.4" height="11" rx="1.2" />
        <path d="M12 8.3l.62 1.48 1.48.62-1.48.62L12 12.5l-.62-1.48-1.48-.62 1.48-.62Z" fill="currentColor" stroke="none" />
    </svg>
);

/** Open palm traced with heart and head lines */
export const PalmistryIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M18 11V6a2 2 0 0 0-4 0v5" />
        <path d="M14 10V4a2 2 0 0 0-4 0v2" />
        <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
        <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.51-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
        <path d="M9.2 14.4c1.9 1.1 4.2.9 5.8-.3M9.9 17.1c1.5.85 3.3.75 4.6-.15" />
    </svg>
);

/** Face being read — scan frame around the features */
export const FaceIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M8 3.5H5.5a2 2 0 0 0-2 2V8" />
        <path d="M16 3.5h2.5a2 2 0 0 1 2 2V8" />
        <path d="M3.5 16v2.5a2 2 0 0 0 2 2H8" />
        <path d="M20.5 16v2.5a2 2 0 0 1-2 2H16" />
        <path d="M9 9.4v1.3M15 9.4v1.3" />
        <path d="M9.2 14.3c1.55 1.35 4.05 1.35 5.6 0" />
    </svg>
);

/** Lo Shu magic square — numbers in their nine houses */
export const NumerologyIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="M9.33 4v16M14.67 4v16M4 9.33h16M4 14.67h16" />
        <path d="M6.67 6.67h.01M12 12h.01M17.33 17.33h.01" />
    </svg>
);

/** Crescent adrift among dream stars */
export const DreamIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M11 4.4a5.6 5.6 0 0 0 8.4 8.4 8.4 8.4 0 1 1-8.4-8.4Z" />
        <path d="M18.4 3.4l.62 1.48 1.48.62-1.48.62-.62 1.48-.62-1.48-1.48-.62 1.48-.62Z" fill="currentColor" stroke="none" />
        <path d="M20.4 10.6h.01" />
    </svg>
);

/** A caring conversation — heart held in a speech bubble */
export const PsychologistIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
        <path d="M12 14.4c-2.1-1.65-3.4-2.85-3.4-4.4a2 2 0 0 1 3.4-1.45A2 2 0 0 1 15.4 10c0 1.55-1.3 2.75-3.4 4.4Z" />
    </svg>
);

/** Globe crossed by planetary lines, places of power marked */
export const AstrocartographyIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...base} {...props}>
        <circle cx="12" cy="12" r="8.75" />
        <ellipse cx="12" cy="12" rx="3.9" ry="8.75" />
        <path d="M4 8.9c2.6-1.4 13.4-1.4 16 0M4 15.1c2.6 1.4 13.4 1.4 16 0" />
        <path d="M8.3 11h.01M15.5 14.4h.01" />
    </svg>
);
